/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  QuerySkillEfficacyInsightsRequest,
  QuerySkillEfficacyInsightsSecurity,
} from "../models/operations/queryskillefficacyinsights.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildSkillEfficacyInsightsInfiniteQuery,
  buildSkillEfficacyInsightsQuery,
  prefetchSkillEfficacyInsights,
  prefetchSkillEfficacyInsightsInfinite,
  queryKeySkillEfficacyInsights,
  queryKeySkillEfficacyInsightsInfinite,
  SkillEfficacyInsightsInfiniteQueryData,
  SkillEfficacyInsightsPageParams,
  SkillEfficacyInsightsQueryData,
} from "./skillEfficacyInsights.core.js";
export {
  buildSkillEfficacyInsightsInfiniteQuery,
  buildSkillEfficacyInsightsQuery,
  prefetchSkillEfficacyInsights,
  prefetchSkillEfficacyInsightsInfinite,
  queryKeySkillEfficacyInsights,
  queryKeySkillEfficacyInsightsInfinite,
  type SkillEfficacyInsightsInfiniteQueryData,
  type SkillEfficacyInsightsPageParams,
  type SkillEfficacyInsightsQueryData,
};

export type SkillEfficacyInsightsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * queryInsights skillEfficacy
 *
 * @remarks
 * Query activation-time skill efficacy, estimated savings, attributed session cost, and optional scored-session detail for the current project. Scores are sampled and costs fan out to every activated version.
 */
export function useSkillEfficacyInsights(
  request?: QuerySkillEfficacyInsightsRequest | undefined,
  security?: QuerySkillEfficacyInsightsSecurity | undefined,
  options?: QueryHookOptions<
    SkillEfficacyInsightsQueryData,
    SkillEfficacyInsightsQueryError
  >,
): UseQueryResult<
  SkillEfficacyInsightsQueryData,
  SkillEfficacyInsightsQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildSkillEfficacyInsightsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * queryInsights skillEfficacy
 *
 * @remarks
 * Query activation-time skill efficacy, estimated savings, attributed session cost, and optional scored-session detail for the current project. Scores are sampled and costs fan out to every activated version.
 */
export function useSkillEfficacyInsightsSuspense(
  request?: QuerySkillEfficacyInsightsRequest | undefined,
  security?: QuerySkillEfficacyInsightsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    SkillEfficacyInsightsQueryData,
    SkillEfficacyInsightsQueryError
  >,
): UseSuspenseQueryResult<
  SkillEfficacyInsightsQueryData,
  SkillEfficacyInsightsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildSkillEfficacyInsightsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * queryInsights skillEfficacy
 *
 * @remarks
 * Query activation-time skill efficacy, estimated savings, attributed session cost, and optional scored-session detail for the current project. Scores are sampled and costs fan out to every activated version.
 */
export function useSkillEfficacyInsightsInfinite(
  request?: QuerySkillEfficacyInsightsRequest | undefined,
  security?: QuerySkillEfficacyInsightsSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    SkillEfficacyInsightsInfiniteQueryData,
    SkillEfficacyInsightsQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<
    SkillEfficacyInsightsInfiniteQueryData,
    SkillEfficacyInsightsPageParams
  >,
  SkillEfficacyInsightsQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    SkillEfficacyInsightsInfiniteQueryData,
    SkillEfficacyInsightsQueryError,
    InfiniteData<
      SkillEfficacyInsightsInfiniteQueryData,
      SkillEfficacyInsightsPageParams
    >,
    QueryKey,
    SkillEfficacyInsightsPageParams
  >({
    ...buildSkillEfficacyInsightsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * queryInsights skillEfficacy
 *
 * @remarks
 * Query activation-time skill efficacy, estimated savings, attributed session cost, and optional scored-session detail for the current project. Scores are sampled and costs fan out to every activated version.
 */
export function useSkillEfficacyInsightsInfiniteSuspense(
  request?: QuerySkillEfficacyInsightsRequest | undefined,
  security?: QuerySkillEfficacyInsightsSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    SkillEfficacyInsightsInfiniteQueryData,
    SkillEfficacyInsightsQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<
    SkillEfficacyInsightsInfiniteQueryData,
    SkillEfficacyInsightsPageParams
  >,
  SkillEfficacyInsightsQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    SkillEfficacyInsightsInfiniteQueryData,
    SkillEfficacyInsightsQueryError,
    InfiniteData<
      SkillEfficacyInsightsInfiniteQueryData,
      SkillEfficacyInsightsPageParams
    >,
    QueryKey,
    SkillEfficacyInsightsPageParams
  >({
    ...buildSkillEfficacyInsightsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setSkillEfficacyInsightsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      skillIds?: Array<string> | undefined;
      from?: Date | undefined;
      to?: Date | undefined;
      includeVersions?: boolean | undefined;
      includeScoredSessions?: boolean | undefined;
      includeSessionCost?: boolean | undefined;
      includeRegressionSignal?: boolean | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: SkillEfficacyInsightsQueryData,
): SkillEfficacyInsightsQueryData | undefined {
  const key = queryKeySkillEfficacyInsights(...queryKeyBase);

  return client.setQueryData<SkillEfficacyInsightsQueryData>(key, data);
}

export function invalidateSkillEfficacyInsights(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      skillIds?: Array<string> | undefined;
      from?: Date | undefined;
      to?: Date | undefined;
      includeVersions?: boolean | undefined;
      includeScoredSessions?: boolean | undefined;
      includeSessionCost?: boolean | undefined;
      includeRegressionSignal?: boolean | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "skillEfficacy",
      "queryInsights",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllSkillEfficacyInsights(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "skillEfficacy", "queryInsights"],
  });
}
